import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Timeline, type TimelineItem } from '@xxtract/ui';

import { useIntakeOrderStatus } from '../hooks/useIntakeOrderStatus';
import type { IntakeOrderStatusProduct, IntakeOrderStatusStep } from '../intake.api';

/**
 * h6-4 — de klant kijkt naar zijn eigen aanmelding.
 *
 * Twee dingen staan hier voorop, en ze zijn allebei een belofte aan de klant:
 *
 * 1. **Er staat nooit niets.** Zolang er geen processtappen zijn, toont de achterkant de ene stap
 *    die we zelf zeker weten — ingediend, en wanneer. "Geen procesdata" hoort een klant nooit te
 *    lezen vlak nadat hij te horen kreeg dat het gelukt is.
 * 2. **Er staat nooit jargon.** Een stap zonder geconfigureerd label krijgt hier een neutrale tekst
 *    uit de vertaallaag, nooit de interne stapcode. De sleutel van een handelseenheid komt niet op
 *    het scherm; hij dient alleen om de tijdlijnen uit elkaar te houden.
 */
export function IntakeOrderStatusPage() {
  const { t, i18n } = useTranslation();
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { data, isLoading, isError, error } = useIntakeOrderStatus(orderNumber ?? null);

  const toonTijd = (waarde: string | null) =>
    waarde ? new Date(waarde).toLocaleString(i18n.language) : '';

  /**
   * De tekst van één stap.
   *
   * Twee soorten "geen label", en ze verdienen niet dezelfde woorden. Een stap uit de bron zonder
   * geconfigureerd label is er een waarvan we niet weten hoe hij heet: dan past een neutraal "In
   * behandeling". De ondergrens komt uit onze eigen aanmelding en daar weten we het wél — dat is
   * "Ontvangen", ook als de mapping nog niet is ingevuld.
   */
  const tekstVan = (stap: { label: string | null; ondergrens: boolean }) =>
    stap.label ?? t(stap.ondergrens ? 'intake.orderStatus.ontvangen' : 'intake.orderStatus.stapZonderLabel');

  const naarTijdlijn = (product: IntakeOrderStatusProduct): TimelineItem[] =>
    product.steps.map((stap: IntakeOrderStatusStep) => ({
      id: stap.id,
      label: tekstVan(stap),
      timestamp: toonTijd(stap.timestamp),
      status: stap.status,
    }));

  if (isLoading) {
    return <p className="p-6 text-[var(--foreground-muted)]">{t('intake.orderStatus.laden')}</p>;
  }

  if (isError || !data) {
    // Een storing is iets anders dan een aanmelding die niet bestaat. Alles op "niet gevonden"
    // gooien laat een klant denken dat zijn aanmelding weg is terwijl er alleen iets haperde.
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return (
      <div className="p-6" role="alert">
        {t(status === 404 ? 'intake.orderStatus.nietGevonden' : 'intake.orderStatus.fout')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t('intake.orderStatus.titel')}</h1>
        <p className="text-[var(--foreground-muted)]">
          {t('intake.orderStatus.ordernummer', { orderNumber: data.orderNumber })}
        </p>
        {/* spec-cp-aanmeldingen-in-lijst CAP-7: "afgerond" is de regel van CustomerPortal, nooit "Ontvangen" als die nee zegt. */}
        <p className="text-lg font-medium">{data.unfinished ? t('intake.list.status.notCompleted') : tekstVan(data.summary)}</p>
      </header>

      {data.degraded && (
        // Nooit rood: de gegevens zijn onvolledig, er is niets stuk aan de aanmelding zelf (NFR4).
        <p className="rounded-md bg-[var(--info-light)] p-3 text-[var(--info-text)]">
          {t('intake.orderStatus.onvolledig')}
        </p>
      )}

      {data.truncated && (
        <p className="rounded-md bg-[var(--info-light)] p-3 text-[var(--info-text)]">
          {t('intake.orderStatus.meerProducten', { aantal: data.products.filter((product) => product.fetched).length })}
        </p>
      )}

      {data.products.map((product, index) => (
        <section key={product.tradeItemId} className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-[var(--foreground-muted)]">
            {t('intake.orderStatus.product', { gtin: product.gtin, nummer: index + 1 })}
          </h2>
          <Timeline
            ariaLabel={t('intake.orderStatus.tijdlijnVoor', { gtin: product.gtin })}
            items={naarTijdlijn(product)}
            showAllLabel={t('intake.orderStatus.toonAlles')}
            showLessLabel={t('intake.orderStatus.toonMinder')}
          />
        </section>
      ))}
    </div>
  );
}
